import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { supabase } from './lib/supabase';
import { AuthProvider } from './store/authStore';
import { useAuthStore } from './store/authStore';
import MainLayout from './components/layout/MainLayout';
import TeamSetup from './components/TeamSetup';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Dashboard from './components/Dashboard';
import TeamHub from './components/TeamHub';
import RosterManagement from './components/PlayerManagement';
import PracticeManagement from './components/PracticeManagement';
import PlaybookManagement from './components/PlaybookManagement';
import GamePlanManagement from './components/gameplan/GamePlanManagement';
import DepthChart from './components/DepthChart';
import { CustomFormationBuilder } from './components/CustomFormationBuilder';

import Settings from './components/Settings';
import Logo from './components/layout/Logo';
import TestAI from './components/TestAI';
import AuthCallback from './pages/AuthCallback';
import SplashPage from './components/SplashPage';
import OnboardingTooltips from './components/OnboardingTooltips';
import ErrorBoundary from './components/ErrorBoundary';
import { SubstitutionReport } from './components/gameplan/SubstitutionReport';
import { PlayerCardList } from './components/player-cards/PlayerCardList';
import { PlayerCardDetail } from './components/player-cards/PlayerCardDetail';
import { PlayerCardForm } from './components/player-cards/PlayerCardForm';
import { GamesManagement } from './components/GamesManagement';
import { GameDetailView } from './components/games/GameDetailView';

const App: React.FC = () => {
  const { isAuthenticated, user, setUser, setSession, setIsAuthenticated, refreshSession } = useAuthStore();
  const [isLoading, setIsLoading] = useState(true);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  useEffect(() => {
    // Check if onboarding has been completed
    const onboardingComplete = localStorage.getItem('onboardingComplete');
    setHasCompletedOnboarding(!!onboardingComplete);
  }, []);

  useEffect(() => {
    const checkAuthState = async () => {
      try {
        await refreshSession();
      } catch (error) {
        console.error('Error checking auth state:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          setIsAuthenticated(false);
          setUser(null);
          setSession(null);
        } else if (session) {
          setIsAuthenticated(true);
          setUser(session.user);
          setSession(session);
        }
      }
    );

    checkAuthState();

    return () => {
      subscription.unsubscribe();
    };
  }, [setIsAuthenticated, setUser, setSession, refreshSession]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-cream">
        <Logo className="h-20 mb-6" />
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        <p className="text-stone mt-4">Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <Toaster 
        position="top-right" 
        toastOptions={{ 
          duration: 5000,
          style: {
            background: '#F7F5F0',
            color: '#3A3C3E',
            border: '1px solid #4DBAAC',
            maxWidth: '100%',
            width: 'auto',
            margin: '0.5rem',
            padding: '1rem',
            borderRadius: '0.5rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        }} 
      />
        <Routes>
          <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
          <Route path="/register" element={!user ? <Register /> : <Navigate to="/" />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          
          {/* Public landing page */}
          <Route path="/" element={!user ? <SplashPage /> : <Navigate to="/dashboard" />} />
          
          {/* Protected routes that require authentication */}
          <Route
            path="/dashboard"
            element={
              user ? (
                <MainLayout>
                  <Dashboard />
                  {!hasCompletedOnboarding && <OnboardingTooltips />}
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/team/:teamId"
            element={
              user ? (
                <MainLayout>
                  <TeamHub />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/team-setup"
            element={
              user ? (
                <MainLayout>
                  <TeamSetup />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/players"
            element={
              user ? (
                <MainLayout>
                  <RosterManagement />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/depth-chart"
            element={
              user ? (
                <MainLayout>
                  <DepthChart />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/practices"
            element={
              user ? (
                <MainLayout>
                  <ErrorBoundary>
                    <PracticeManagement />
                  </ErrorBoundary>
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route path="/playbook" element={
            <MainLayout>
              <PlaybookManagement />
            </MainLayout>
          } />
          <Route
            path="/game-plans"
            element={
              user ? (
            <MainLayout>
                  <ErrorBoundary>
              <GamePlanManagement />
                  </ErrorBoundary>
            </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route path="/settings" element={
            <MainLayout>
              <Settings />
            </MainLayout>
          } />
          {/* Coach Steve is now an off-canvas panel from the FAB; redirect old URL to dashboard */}
          <Route path="/coach-steve" element={<Navigate to="/dashboard" replace />} />
          <Route
            path="/games"
            element={
              user ? (
                <MainLayout>
                  <GamesManagement />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/games/:gameId"
            element={
              user ? (
                <MainLayout>
                  <GameDetailView />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/player-cards"
            element={
              user ? (
                <MainLayout>
                  <PlayerCardList />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/player-cards/:playerId"
            element={
              user ? (
                <MainLayout>
                  <PlayerCardDetail />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/player-cards/:playerId/edit"
            element={
              user ? (
                <MainLayout>
                  <PlayerCardForm />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/player-cards/new"
            element={
              user ? (
                <MainLayout>
                  <PlayerCardForm />
                </MainLayout>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route path="/test-ai" element={
            <MainLayout>
              <TestAI />
            </MainLayout>
          } />
          <Route 
            path="/teams/:teamId/substitution-report" 
            element={<SubstitutionReportWrapper />} 
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
    </Router>
  );
};

const SubstitutionReportWrapper = () => {
  const { teamId } = useParams();
  return <SubstitutionReport teamId={teamId || ''} />;
};

export default App;